import { useState } from "react";

import { useMediaQuery } from "@/hooks/use-media-query";
import type { IncluirFichaAnexoPayload } from "@/modules/financeiro-fichas";
import { formatarTamanhoAnexo } from "@/shared/components";
import { MOBILE_QUERY } from "../../../financeiro-obras.constants";
import type { AnexoPendente } from "../../hooks/use-gerar-ficha";
import type { FichaAnexosPreviaProps } from "./types-ficha-anexos-previa";

/** Extensão do arquivo em maiúsculas (PDF, PNG...), como o formato dos anexos já salvos. */
function formatoDoArquivo(nome: string): string {
  const ponto = nome.lastIndexOf(".");
  return ponto >= 0 ? nome.slice(ponto + 1).toUpperCase() : "";
}

/**
 * Anexos da prévia (geração da ficha): a janela de inclusão só guarda o arquivo em memória, e a
 * lixeira pede confirmação e só então o tira da lista. Nada vai ao servidor aqui; o Salvar da ficha envia tudo.
 */
export function useFichaAnexosPrevia({
  title,
  anexos,
  adicionar,
  remover,
  bloqueado = false,
}: FichaAnexosPreviaProps) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [incluindo, setIncluindo] = useState(false);
  const [removendo, setRemovendo] = useState<AnexoPendente | null>(null);

  const linhas = anexos.map((anexo) => ({
    id: anexo.id,
    identificacao: anexo.identificacao,
    formato: formatoDoArquivo(anexo.arquivo.name),
    tamanho: formatarTamanhoAnexo(anexo.arquivo.size),
    testIds: {
      linha: `ficha-anexo-${anexo.id}`,
      remover: `ficha-anexo-remover-${anexo.id}`,
    },
  }));

  async function salvarInclusao(payload: IncluirFichaAnexoPayload): Promise<boolean> {
    adicionar(payload);
    setIncluindo(false);
    return true;
  }

  return {
    isMobile,
    title,
    linhas,
    bloqueado,
    incluindo,
    abrirInclusao: () => setIncluindo(true),
    cancelarInclusao: () => setIncluindo(false),
    salvarInclusao,
    removendoIdentificacao: removendo?.identificacao ?? null,
    pedirRemocao: (id: string) => setRemovendo(anexos.find((anexo) => anexo.id === id) ?? null),
    cancelarRemocao: () => setRemovendo(null),
    confirmarRemocao: () => {
      if (removendo) remover(removendo.id);
      setRemovendo(null);
    },
  };
}
